import { useQuery } from '@tanstack/react-query';
import { CalendarDays, MapPin, Settings2 } from 'lucide-react';
import { fetchEvenementsPublics } from '../services/api';
import { nomEdition, periodeEvenement } from '../utils/evenement';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Mention du repas sur la carte d'un événement.
 *
 * Trois états, calculés par le serveur : ouvert, complet — plus de couvert
 * adulte — et clos, la date de clôture ou celle du service étant passée. Un
 * repas fermé se signale quand même : l'information intéresse le visiteur, et
 * lui laisser ouvrir la fiche pour l'apprendre serait une perte de temps.
 */
function MentionRepas({ evenement }: { evenement: any }) {
  const etats: Record<string, { libelle: string; classe: string }> = {
    ouvert: { libelle: 'Repas sur réservation', classe: 'text-red-600 dark:text-red-400' },
    complet: { libelle: 'Repas — Complet', classe: 'text-gray-500 dark:text-gray-400' },
    clos: { libelle: 'Repas — Réservations closes', classe: 'text-gray-500 dark:text-gray-400' },
  };

  const etat = etats[evenement.repasEtat as string] ?? etats.ouvert;
  const estOuvert = (evenement.repasEtat ?? 'ouvert') === 'ouvert';

  return (
    <p className={`mt-2 inline-flex items-center gap-1.5 text-sm font-semibold ${etat.classe}`}>
      <span aria-hidden="true">🍽️</span>
      {etat.libelle}
      {estOuvert && evenement.repasDate && (
        <span className="font-normal">
          — {format(new Date(evenement.repasDate), "d MMMM 'à' HH'h'mm", { locale: fr })}
        </span>
      )}
    </p>
  );
}

/**
 * Mention du concert sur la carte d'un événement.
 *
 * Mêmes trois états que le repas, et la même raison de les afficher : le
 * visiteur doit savoir depuis la liste si la billetterie est encore ouverte.
 */
function MentionConcert({ evenement }: { evenement: any }) {
  const etats: Record<string, { libelle: string; classe: string }> = {
    ouvert: { libelle: 'Concert sur réservation', classe: 'text-violet-700 dark:text-violet-400' },
    complet: { libelle: 'Concert — Complet', classe: 'text-gray-500 dark:text-gray-400' },
    clos: { libelle: 'Concert — Réservations closes', classe: 'text-gray-500 dark:text-gray-400' },
  };

  const etat = etats[evenement.concertEtat as string] ?? etats.ouvert;
  const estOuvert = (evenement.concertEtat ?? 'ouvert') === 'ouvert';

  return (
    <p className={`mt-2 inline-flex items-center gap-1.5 text-sm font-semibold ${etat.classe}`}>
      <span aria-hidden="true">🎵</span>
      {etat.libelle}
      {estOuvert && evenement.concertDate && (
        <span className="font-normal">
          — {format(new Date(evenement.concertDate), "d MMMM 'à' HH'h'mm", { locale: fr })}
        </span>
      )}
    </p>
  );
}

export default function Evenements() {
  const { user } = useAuth();
  // Calcule par le serveur : au bureau, seul le president gere les evenements.
  const peutGerer = user?.peutGererEvenements === true;

  const { data: evenements, isLoading } = useQuery({
    queryKey: ['evenements-publics'],
    queryFn: fetchEvenementsPublics,
  });

  return (
    <div className="p-4 lg:p-0 space-y-4 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Nos événements</h1>
        {peutGerer && (
          <Link
            to="/bureau/evenements"
            className="btn-outline inline-flex items-center gap-2 text-sm px-3 py-1.5"
          >
            <Settings2 className="w-4 h-4" /> Gérer les événements
          </Link>
        )}
      </div>

      {isLoading && (
        <p className="text-gray-500 dark:text-gray-400">Chargement des événements…</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {evenements?.map((evt: any) => (
          <Link
            key={evt.id}
            to={`/evenements/${evt.slug ?? evt.id}`}
            className="card overflow-hidden lg:p-0 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 block hover:shadow-md hover:border-primary-200 dark:hover:border-primary-700 transition"
          >
            {evt.imageUrl && (
              <div className="relative">
                <img
                  src={evt.imageUrl}
                  alt={evt.nom}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold bg-primary-600/90 text-white backdrop-blur-sm">
                  {format(new Date(evt.dateDebut), 'dd/MM/yyyy', { locale: fr })}
                </div>
              </div>
            )}
            <div className="p-4 lg:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{nomEdition(evt.nom, evt.dateDebut)}</h2>

              {/* Le repas est ce qui décide un villageois à cliquer : il se
                  signale sur la carte, sans attendre l'ouverture de la fiche.
                  L'état évite de faire cliquer pour rien sur un repas complet
                  ou dont les réservations sont fermées. */}
              {evt.repasActif && <MentionRepas evenement={evt} />}
              {evt.concertActif && <MentionConcert evenement={evt} />}

              <div className="mt-3 flex flex-wrap gap-y-2 gap-x-4 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      <span className="first-letter:uppercase">{periodeEvenement(evt.dateDebut, evt.dateFin)}</span>
                </div>
              </div>
              {evt.lieu && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <MapPin className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                  {evt.lieu}
                </div>
              )}
              {evt.description && (
                <p className="mt-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-4">{evt.description}</p>
              )}
              <p className="mt-4 text-sm font-medium text-primary-700 dark:text-primary-400">
                Voir l'événement
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
